import {
  composeRenderProps,
  Button as AriaButton,
  ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { focusRing } from 'src/utils';
import LoadingIcon from 'src/assets/icons/loader.svg';

import type { ColorVariant, IconProps } from 'src/types/common';

type AccessibleNameProps =
  | {
      'aria-label': string;
      'aria-labelledby'?: never;
    }
  | {
      'aria-label'?: never;
      'aria-labelledby': string;
    };

const iconButton = tv({
  extend: focusRing,
  base: 'inline-flex justify-center flex-shrink-0 items-center transition',
  variants: {
    variant: {
      surface: 'text-surface-alt-ghost-75 hover:bg-surface-alt-ghost',
      primary: 'text-primary-ghost-60 hover:bg-primary-ghost',
      secondary: 'text-secondary-ghost-60 hover:bg-secondary-ghost',
      success: 'text-green-ghost-alt-60 hover:bg-green-ghost-alt',
      danger: 'text-red-ghost-alt-60 hover:bg-red-ghost-alt',
      warning: 'text-yellow-ghost-alt-60 hover:bg-yellow-ghost-alt',
      ai: 'text-ai-ghost-alt-60 hover:bg-ai-ghost-alt',
    },
    isDisabled: {
      true: 'cursor-not-allowed !text-surface-alt-ghost-30',
    },
    isFlat: {
      true: 'w-10 h-10 rounded-full shadow-3',
    },
    size: {
      small: '',
      medium: '',
    },
  },
  defaultVariants: {
    variant: 'surface',
    isFlat: false,
    size: 'medium',
  },
  compoundVariants: [
    {
      isFlat: false,
      size: 'medium',
      className: 'w-11 h-11 rounded-lg',
    },
    {
      isFlat: false,
      size: 'small',
      className: 'w-8 h-8 rounded-sm',
    },
  ],
});

export type IconButtonProps = Omit<AriaButtonProps, 'children'> &
  AccessibleNameProps & {
    variant?: ColorVariant;
    size?: 'small' | 'medium';
    Icon?: IconProps;
    isFlat?: boolean;
    loading?: boolean;
  };

const IconButton = ({
  variant = 'surface',
  Icon = () => null,
  isFlat = false,
  loading = false,
  size = 'medium',
  ...props
}: IconButtonProps) => {
  const IconComponent = loading ? LoadingIcon : Icon;

  return (
    <AriaButton
      {...props}
      aria-disabled={loading || props.isDisabled || undefined}
      className={composeRenderProps(
        props.className,
        (className, renderProps) => {
          return iconButton({
            ...renderProps,
            variant,
            isFlat,
            size,
            className,
          });
        }
      )}
    >
      <IconComponent
        aria-hidden="true"
        className={`${isFlat || size === 'small' ? 'text-base' : 'text-xl'} text-inherit ${loading ? 'animate-spin' : ''}`}
      />
    </AriaButton>
  );
};

export default IconButton;
